/* eslint-disable react/prop-types */
import { useEffect, useMemo, useState } from "react";

import { Grid, TextField } from "@mui/material";

import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import Footer from "examples/Footer";
import DataTable from "examples/Tables/DataTable";

import useApi from "../../../components/Utils/api";
import toast from "react-hot-toast";
import { DateRangePicker } from "rsuite";
import { afterToday } from "rsuite/cjs/DateRangePicker/disabledDateUtils";
import reportsLineChartData from "../../dashboard/data/reportsLineChartData";

function CardSettlementSummary({ embedded = false, tabsSlot = null } = {}) {
  const { predefinedRanges } = reportsLineChartData;
  const [summary, setSummary] = useState([]);
  const [dateRange, setDateRange] = useState(() => {
    const today = new Date();
    return [today, today];
  });
  const [filterMid, setFilterMid] = useState("");
  const api = useApi();

  const filteredSummary = useMemo(() => {
    const mid = filterMid.trim().toLowerCase();
    if (!mid) return summary;
    return summary.filter((item) => item.mid?.toLowerCase().includes(mid));
  }, [summary, filterMid]);

  const formatDate = (date) => {
    const d = new Date(date);
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${month}/${day}/${year}`;
  };

  const getCardSettlementSummary = (range) => {
    if (!range) return;
    setDateRange(range);
    api.post("settlements/summary", { startDate: formatDate(range[0]), endDate: formatDate(range[1]) })
      .then((response) => {
        setSummary(response.data.data.settlementSummary || []);
      })
      .catch((error) => {
        if (error.response?.status === 403) {
          toast.error("You do not have permission to perform this action.", { id: "PermissionError" });
          return;
        }
        // No matching statements is surfaced by the table's own empty state, not a toast
        setSummary([]);
      });
  };

  const columns = useMemo(() => [
    { Header: "MID", accessor: "mid", width: "15%" },
    { Header: "Statement Date", accessor: "statementDate", width: "15%" },
    { Header: "Batch ID", accessor: "batchId", width: "20%" },
    { Header: "Total Credit", accessor: "totalCredit", width: "15%" },
    { Header: "Total Comm", accessor: "totalComm", width: "15%" },
    { Header: "Settlement Date", accessor: "settlementDate", detailOnly: true },
    { Header: "Start Time", accessor: "startTime", detailOnly: true },
    { Header: "End Time", accessor: "endTime", detailOnly: true },
    { Header: "Opening Amount", accessor: "openingAmount", detailOnly: true },
    { Header: "Settlement Amount", accessor: "settlementAmount", detailOnly: true },
    { Header: "Total Counts", accessor: "totalCounts", detailOnly: true },
    { Header: "Total Debit", accessor: "totalDebit", detailOnly: true },
    { Header: "Total Vat", accessor: "totalVat", detailOnly: true },
  ], []);

  useEffect(() => {
    getCardSettlementSummary(dateRange);
  }, []);

  const content = (
      <MDBox mt={3} mb={3}>
        <Grid container spacing={3} justifyContent="center">
          <Grid item xs={12}>
            <Grid container alignItems="center">
              <Grid item xs={4}>
                <MDBox>
                  <MDTypography variant="h5" fontWeight="medium">
                    Statement
                  </MDTypography>
                  <MDTypography variant="button" fontWeight="regular" color="secondary">
                    {filteredSummary.length} Statements
                  </MDTypography>
                </MDBox>
              </Grid>
              <Grid item xs={4} display="flex" justifyContent="center" alignItems="center" gap={2}>
                <DateRangePicker
                  value={dateRange}
                  disabledDate={afterToday()}
                  ranges={predefinedRanges}
                  onChange={(range) => getCardSettlementSummary(range)}
                  format="d MMMM yyyy"
                />
                <TextField
                  label="MID"
                  placeholder="Search by MID"
                  value={filterMid}
                  onChange={(e) => setFilterMid(e.target.value)}
                  size="small"
                  variant="outlined"
                  sx={{ minWidth: 200, "& .MuiOutlinedInput-root": { height: "38px" } }}
                />
              </Grid>
              <Grid item xs={4} />
            </Grid>
          </Grid>
          {tabsSlot && (
            <Grid item xs={12}>
              {tabsSlot}
            </Grid>
          )}
          <Grid item xs={12} lg={12}>
            <MDBox sx={(theme) => ({ border: `1px solid ${theme.palette.grey[300]}`, borderRadius: "8px", background: theme.palette.white.main, overflow: "hidden" })}>
              <DataTable
                table={{ columns, rows: filteredSummary }}
                showTotalEntries
                isSorted
                noEndBorder
                entriesPerPage={{ defaultValue: 10, entries: [10, 25, 50, 100] }}
                entriesPerPagePosition="bottom"
                canSearch={false}
                dialogTitle="Settlement Details"
              />
            </MDBox>
          </Grid>
        </Grid>
      </MDBox>
  );

  // embedded=true renders content only (used as a tab inside layouts/settlement)
  if (embedded) return content;

  return (
    <DashboardLayout>
      {content}
      <Footer />
    </DashboardLayout>
  );
}

export default CardSettlementSummary;
